import { DateFilter } from 'containers/payouts/payouts.model';
import { FC, useEffect, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import DateUtils from 'utils/dateUtils';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface DateFilterGroupProps {
  title: string;
  options: Record<string, string>;
  selectedOption: string | null;
  onOptionClick: (option: string) => void;
  dateRange: DateFilter;
  onDateRangeChange: (dateRange: DateFilter) => void;
  dateRangeKey: string;
  allowPastDate?: boolean;
  allowFutureDate?: boolean;
  customRanges?: StaticRange[];
}

const DateFilterGroup: FC<DateFilterGroupProps> = ({
  title,
  options,
  selectedOption,
  onOptionClick,
  dateRange,
  onDateRangeChange,
  allowPastDate = true,
  allowFutureDate = true
}) => {
  const getDefaultRange = () => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return [
        {
          startDate: new Date(dateRange.startDate),
          endDate: new Date(dateRange.endDate),
          key: 'selection'
        }
      ];
    }

    const today = new Date();
    return [
      {
        startDate: today,
        endDate: today,
        key: 'selection'
      }
    ];
  };

  const [ranges, setRanges] = useState(getDefaultRange());
  const [showDateRange, setShowDateRange] = useState(false);

  useEffect(() => {
    const calendar = document.querySelector('.rdrDefinedRangesWrapper');
    if (calendar) {
      (calendar as HTMLElement).style.width = '0';
    }
  }, [selectedOption, showDateRange]);

  const handleOptionClick = (option: string) => {
    onOptionClick(option);

    if (option === options.DATE_RANGE) {
      setShowDateRange(true);
    } else {
      setShowDateRange(false);
      setRanges(getDefaultRange());
    }
  };

  const handleRangeChange = ranges_ => {
    setRanges([ranges_.selection]);
  };

  const handleDateRangeApply = () => {
    onDateRangeChange({
      startDate: DateUtils.getUTCStartDate(ranges[0].startDate),
      endDate: DateUtils.getUTCEndDate(ranges[0].endDate),
      option: selectedOption
    });
    setShowDateRange(false);
  };

  const getOptionLabel = (option: string) => {
    if (option === options.DATE_RANGE) {
      if (dateRange.startDate && dateRange.endDate && selectedOption === options.DATE_RANGE) {
        const startDate = DateUtils.getDateInFormat({
          date: new Date(dateRange.startDate),
          dateFormat: 'MMM dd, yyyy'
        });
        const endDate = DateUtils.getDateInFormat({ date: new Date(dateRange.endDate), dateFormat: 'MMM dd, yyyy' });
        const label = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
        return label;
      }
      return option;
    }
    return option;
  };

  return (
    <div className="space-y-4">
      <div className="mt-5 text-sbase font-semibold text-primaryText">{title}</div>
      <div className="mt-2 flex flex-wrap gap-2.5">
        {Object.values(options).map(option => (
          <button
            key={option}
            onClick={() => handleOptionClick(option)}
            className={`
            rounded-full border px-5 py-1 text-sbase text-primaryText transition-colors 
              ${selectedOption === option ? 'border-secondary bg-secondaryBtn' : 'border-borderGray hover:bg-gray-50'}
            `}>
            {getOptionLabel(option)}
          </button>
        ))}
      </div>

      {showDateRange && (
        <div
          className="mt-4 flex w-fit flex-col justify-center rounded-md border-borderGray
           bg-[#f9fafc] shadow-md">
          <DateRangePicker
            startDatePlaceholder="Start Date"
            endDatePlaceholder="End Date"
            rangeColors={['#A4D7FA']}
            ranges={ranges}
            onChange={handleRangeChange}
            staticRanges={[]}
            inputRanges={[]}
            {...(!allowFutureDate && { maxDate: new Date() })}
            {...(!allowPastDate && { minDate: new Date() })}
          />
          <div
            id={'date-range-okay-button'}
            onClick={handleDateRangeApply}
            className="ml-auto px-4 pb-3 text-sbase font-bold text-secondary hover:cursor-pointer">
            Okay
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterGroup;
